import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createInterview, fetchCandidates, fetchJobs } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/contexts/ToastContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const INTERVIEW_TYPES = [
  { value: 'phone', label: 'Phone' },
  { value: 'video', label: 'Video' },
  { value: 'onsite', label: 'On-site' },
  { value: 'panel', label: 'Panel' },
];

interface InterviewSchedulerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fill candidateId when scheduling from a candidate profile */
  candidateId?: string;
  /** Pre-fill jobId when scheduling from a job detail page */
  jobId?: string;
  onSuccess?: () => void;
}

export function InterviewScheduler({
  open,
  onOpenChange,
  candidateId: initialCandidateId,
  jobId: initialJobId,
  onSuccess,
}: InterviewSchedulerProps) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const [title, setTitle] = useState('');
  const [type, setType] = useState('phone');
  const [candidateId, setCandidateId] = useState(initialCandidateId || '');
  const [jobId, setJobId] = useState(initialJobId || '');
  const [scheduledAt, setScheduledAt] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [location, setLocation] = useState('');
  const [meetingLink, setMeetingLink] = useState('');
  const [notes, setNotes] = useState('');

  // Fetch candidates for dropdown
  const { data: candidatesData } = useQuery({
    queryKey: ['candidates', { limit: 100 }],
    queryFn: () => fetchCandidates({ limit: 100 }),
    enabled: open,
  });

  // Fetch jobs for dropdown
  const { data: jobsData } = useQuery({
    queryKey: ['jobs', { limit: 100 }],
    queryFn: () => fetchJobs({ limit: 100 }),
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: () =>
      createInterview({
        candidateId,
        jobId,
        title,
        type,
        scheduledAt: new Date(scheduledAt).toISOString(),
        durationMinutes,
        location: location || undefined,
        meetingLink: meetingLink || undefined,
        notes: notes || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] });
      addToast({ type: 'success', title: 'Interview scheduled successfully' });
      resetForm();
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error: Error) => {
      addToast({ type: 'error', title: 'Failed to schedule interview', message: error.message });
    },
  });

  const resetForm = () => {
    setTitle('');
    setType('phone');
    setCandidateId(initialCandidateId || '');
    setJobId(initialJobId || '');
    setScheduledAt('');
    setDurationMinutes(60);
    setLocation('');
    setMeetingLink('');
    setNotes('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!candidateId || !jobId || !title.trim() || !scheduledAt) return;
    mutation.mutate();
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) resetForm();
    onOpenChange(isOpen);
  };

  const candidates = candidatesData?.data ?? [];
  const jobs = jobsData?.data ?? [];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Schedule Interview</DialogTitle>
          <DialogDescription>
            Fill in the details below to schedule a new interview.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Candidate */}
          <div className="space-y-2">
            <Label>Candidate</Label>
            <Select
              value={candidateId}
              onValueChange={setCandidateId}
              disabled={!!initialCandidateId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select candidate" />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.firstName} {c.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Job */}
          <div className="space-y-2">
            <Label>Job</Label>
            <Select
              value={jobId}
              onValueChange={setJobId}
              disabled={!!initialJobId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select job" />
              </SelectTrigger>
              <SelectContent>
                {jobs.map((j) => (
                  <SelectItem key={j.id} value={j.id}>
                    {j.title}{j.company ? ` - ${j.company.name}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="sched-title">Title</Label>
            <Input
              id="sched-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Technical Interview Round 1"
              required
            />
          </div>

          {/* Type & Duration */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {INTERVIEW_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sched-duration">Duration (minutes)</Label>
              <Input
                id="sched-duration"
                type="number"
                min={5}
                max={480}
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(Number(e.target.value))}
              />
            </div>
          </div>

          {/* Scheduled At */}
          <div className="space-y-2">
            <Label htmlFor="sched-at">Scheduled At</Label>
            <Input
              id="sched-at"
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              required
            />
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="sched-location">Location</Label>
            <Input
              id="sched-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Office address or room name"
            />
          </div>

          {/* Meeting Link */}
          <div className="space-y-2">
            <Label htmlFor="sched-link">Meeting Link</Label>
            <Input
              id="sched-link"
              value={meetingLink}
              onChange={(e) => setMeetingLink(e.target.value)}
              placeholder="https://..."
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="sched-notes">Notes</Label>
            <Textarea
              id="sched-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || !candidateId || !jobId || !title.trim() || !scheduledAt}
            >
              {mutation.isPending ? 'Scheduling...' : 'Schedule Interview'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
